// @LTG-ID: data/journals/selling-a-medical-practice
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 33 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Industry-Specific (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "selling-a-medical-practice",
    FRONTMATTER: {
        number: "70",
        section: "Industry-Specific",
        sectionHref: "/journals#industry-specific",
        sectionAnchor: "industry-specific",
        title: "Selling a Medical Practice: What Physician Owners Should Understand",
        deck: "Medical practices sell to hospital systems, private equity backed platforms, and other physicians, with value driven by provider capacity beyond the owner, referral durability, payer mix, and compliance standing.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-22",
        publishedHuman: "June 22, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 493,
        timeRequired: "PT3M",
        keywords: "selling a medical practice, medical practice valuation, physician practice sale, private equity medical practices",
        articleSection: "Industry-Specific",
        articleBodyPreview: "Medical practices sell to hospital systems, private equity backed platforms, and other physicians, with value driven by provider capacity beyond the owner, referral durability, payer mix, and compliance standing. Healthcare transactions carry regulatory requirements, including fraud and abuse and corporate practice rules, that make specialized healthcare counsel essential from the start. A medical practice sale is a business transaction wrapped in a regulatory environment that most business advi",
        complianceNote: "This article is educational and does not constitute legal, financial, or medical advice. Healthcare transactions require specialized regulatory counsel.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "customer-concentration-selling-business",
        "due-diligence-selling-a-business",
        "selling-a-dental-practice"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Selling a Medical Practice: A Physician Owner's Guide · LTG",
        canonical: "https://letstalkgrowth.com/journals/selling-a-medical-practice",
        ogImage: "/journals/og/selling-a-medical-practice.jpg",
    },
    BODY_MARKDOWN: `Medical practices sell to hospital systems, private equity backed platforms, and other physicians, with value driven by provider capacity beyond the owner, referral durability, payer mix, and compliance standing. Healthcare transactions carry regulatory requirements, including fraud-and-abuse and corporate practice rules, that make specialized healthcare counsel essential from the start.

A medical practice sale is a business transaction wrapped in a regulatory environment that most business advice ignores. Physician owners need both halves of the picture.

## The buyer landscape

Three buyer families dominate. Hospital and health system acquirers buy practices for referral alignment and network strategy, and their deals often convert the physician into employment. Private equity backed platforms, typically structured through management services organizations, have consolidated heavily across specialties, dermatology, ophthalmology, orthopedics, primary care among them, a movement extensively documented in health policy research and the trade press; their offers frequently include rollover equity and multi-year practice commitments, close cousins of the DSO structures described in our dental article. And physician buyers, individuals or groups, remain the traditional path, especially for primary care and smaller practices.

As with dentistry, the structural point matters more than the headline: sales to institutional buyers usually purchase the physician's future working years along with the practice, and should be evaluated with the personal-readiness lens this series applies to every exit.

## What buyers evaluate

The universal drivers apply with clinical accents. Provider capacity beyond the selling physician, employed physicians, advanced practice providers, determines whether the practice is a business or a personal clinical income stream. Referral relationships and their durability under new ownership get tested in diligence. Payer mix, contract rates, and the revenue cycle's health are examined line by line. And compliance standing, billing and coding accuracy, licensure, documentation, functions as a gate: problems here do not discount a deal so much as end it, since buyers inherit regulatory exposure.

## The regulatory layer that changes everything

Healthcare transactions run through rules that ordinary business sales never meet: federal fraud-and-abuse laws that constrain how deals and post-sale compensation can be structured, state corporate practice of medicine doctrines that dictate who may own clinical entities and force the MSO structures buyers use, patient record and privacy obligations governing the transition, and payer contract and enrollment mechanics that determine when the buyer can actually bill. None of this is a reason not to sell; all of it is the reason the first advisor hired for a medical practice sale should be healthcare transaction counsel, before the letter of intent rather than after.

## The physician's preparation list

The familiar program, with clinical specifics: build provider capacity so production survives your reduced role, run an internal billing and compliance review before a buyer runs theirs, organize the practice's financials to diligence standard, and confirm the lease and equipment picture. Then do the personal math honestly, because institutional offers that require five more working years are asking a question about your life that no valuation answers.`,
    FAQ: [
        { question: "Who buys medical practices?", answer: "Hospital systems, private equity backed platforms operating through MSO structures, and other physicians. Each pays for different attributes and imposes different post-sale obligations, from employment to multi-year commitments with rollover equity." },
        { question: "Why do medical practice sales need specialized attorneys?", answer: "Because federal fraud-and-abuse laws, state corporate practice of medicine rules, privacy obligations, and payer enrollment mechanics constrain deal structure in ways general business counsel rarely handles. Healthcare transaction counsel should be engaged before serious negotiations." },
        { question: "What lowers a medical practice's value most?", answer: "Total dependence on the selling physician's personal production, deteriorating payer contracts, and compliance problems in billing and documentation. The first is fixable with provider development; the last must be fixed before any process begins." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Value Drivers",
            title: "Customer Concentration: How Much Is Too Much When Selling a Business?",
            meta: "3 min · Brief 50",
            to: "/journals/customer-concentration-selling-business",
        },
        {
            tag: "Deal Process",
            title: "What Is Due Diligence When Selling a Business? A Seller's Survival Guide",
            meta: "3 min · Brief 56",
            to: "/journals/due-diligence-selling-a-business",
        },
        {
            tag: "Industry-Specific",
            title: "Selling a Dental Practice: Valuation Dynamics, DSOs, and Doctor Transitions",
            meta: "3 min · Brief 69",
            to: "/journals/selling-a-dental-practice",
        },
    ],
};

export default BRIEF;
